import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { RunTypeBadge } from '../../components/common/RunTypeBadge';
import { StatusBadge } from '../../components/common/StatusBadge';
import { DEPLOYMENT_STATUS_STYLES, MODEL_TYPE_COLORS } from '../../constants';
import { resultSummary } from '../../metrics';
import { EvalApiService } from '../../services/EvalApiService';
import { EvalModel, EvalRun } from '../../types';
import { fmtIso } from '../../utils';

interface DatasetGroup {
  dataset_id: string;
  dataset_name: string | null;
  runs: EvalRun[];
}

function groupByDataset(runs: EvalRun[]): DatasetGroup[] {
  const map = new Map<string, DatasetGroup>();
  for (const run of runs) {
    if (!map.has(run.dataset_id)) {
      map.set(run.dataset_id, {
        dataset_id: run.dataset_id,
        dataset_name: run.dataset_name,
        runs: [],
      });
    }
    map.get(run.dataset_id)!.runs.push(run);
  }
  // Sort groups by most recent run within each
  const groups = [...map.values()];
  groups.sort((a, b) => {
    const aLatest = a.runs[0]?.triggered_at ?? '';
    const bLatest = b.runs[0]?.triggered_at ?? '';
    return bLatest.localeCompare(aLatest);
  });
  return groups;
}

export const ModelDetailPage: React.FC = () => {
  const { modelId } = useParams<{ modelId: string }>();
  const { token } = useAuthContext();
  const [model, setModel] = useState<EvalModel | null>(null);
  const [runs, setRuns] = useState<EvalRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!modelId) return;
    EvalApiService.getModel(token ?? '', modelId).then((m) => {
      if (!m) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setModel(m);
      EvalApiService.listRuns(token ?? '', { model_id: m.service_model_id, limit: 200 }).then(
        ({ items }) => {
          setRuns(items);
          setLoading(false);
        }
      );
    });
  }, [modelId, token]);

  if (loading) {
    return (
      <div className="page-content">
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading...</div>
      </div>
    );
  }

  if (notFound || !model) {
    return (
      <div className="page-content">
        <div style={{ color: 'var(--danger)', fontSize: 13 }}>Model not found.</div>
      </div>
    );
  }

  const typeStyle = MODEL_TYPE_COLORS[model.model_type];
  const groups = groupByDataset(runs);

  return (
    <div className="page-content">
      <div className="eval-page-header">
        <div>
          <div style={{ marginBottom: 4 }}>
            <Link to="/eval/models" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              ← Models
            </Link>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="eval-page-title" style={{ fontFamily: 'var(--font-mono)' }}>
              {model.version}
            </div>
            <span
              style={{
                ...typeStyle,
                display: 'inline-flex',
                alignItems: 'center',
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: 4,
              }}
            >
              {model.model_type}
            </span>
            <span style={{ ...DEPLOYMENT_STATUS_STYLES[model.deployment_status], fontSize: 12 }}>
              {model.deployment_status.charAt(0).toUpperCase() + model.deployment_status.slice(1)}
            </span>
          </div>
          {model.description && <div className="eval-page-sub">{model.description}</div>}
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: 12,
          marginBottom: 32,
        }}
      >
        <div className="eval-info-card">
          <div className="eval-info-label">Service Model ID</div>
          <div
            className="eval-info-value"
            style={{ fontFamily: 'var(--font-mono)', fontSize: 11, wordBreak: 'break-all' }}
          >
            {model.service_model_id}
          </div>
        </div>
        <div className="eval-info-card">
          <div className="eval-info-label">Registered At</div>
          <div className="eval-info-value">{fmtIso(model.registered_at)}</div>
        </div>
        {model.registered_by && (
          <div className="eval-info-card">
            <div className="eval-info-label">Registered By</div>
            <div className="eval-info-value">{model.registered_by}</div>
          </div>
        )}
        <div className="eval-info-card">
          <div className="eval-info-label">Total Runs</div>
          <div className="eval-info-value">{runs.length}</div>
        </div>
      </div>

      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--text-secondary)',
          marginBottom: 16,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        Runs by Dataset
      </div>

      {groups.length === 0 && (
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
          No runs found for this model.
        </div>
      )}

      {groups.map((group) => (
        <div key={group.dataset_id} style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
            <Link
              to={`/eval/datasets/${group.dataset_id}`}
              style={{ fontSize: 14, fontWeight: 600, color: 'var(--accent)' }}
            >
              {group.dataset_name ?? group.dataset_id}
            </Link>
            {group.dataset_name && (
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  color: 'var(--text-tertiary)',
                }}
              >
                {group.dataset_id}
              </span>
            )}
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 4 }}>
              {group.runs.length} run{group.runs.length !== 1 ? 's' : ''}
            </span>
          </div>
          <table className="admin-table" style={{ marginBottom: 0 }}>
            <thead>
              <tr>
                <th>Run ID</th>
                <th>Type</th>
                <th>Status</th>
                <th>Triggered At</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {group.runs.map((run) => (
                <tr key={run.run_id}>
                  <td>
                    <Link
                      to={`/eval/runs/${run.run_id}`}
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 12,
                        color: 'var(--accent)',
                      }}
                    >
                      {run.run_id}
                    </Link>
                  </td>
                  <td>
                    <RunTypeBadge runType={run.run_type} />
                  </td>
                  <td>
                    <StatusBadge status={run.status} />
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {fmtIso(run.triggered_at)}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                    {resultSummary(run.result)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};
